import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { isAdmin } from '../../lib/roles';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import type { CompraListItem } from '../../types/domain';

const COP = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

/** Módulo de Compras (DAF v2, sección 17). Toda compra se registra vía
 * el RPC `registrar_compra` (supabase/schema_compras.sql), que además
 * genera la entrada de inventario correspondiente y, si es a crédito, la
 * cuenta por pagar — nunca se hace un insert directo a `compras`.
 *
 * Corrección de compras mal registradas (pedido explícito del usuario,
 * después de tener que arreglarlo a mano por SQL Editor una vez):
 * `registrar_compra()` decide crédito vs. contado con un parámetro al
 * momento de crear la compra, sin guardarlo como columna -- así que la
 * columna "Forma de pago" de abajo se calcula viendo si existe una
 * `cuenta_por_pagar` para esa compra, no leyendo un campo directo. Si a
 * alguien se le pasó la mano y marcó "contado" una compra que era a
 * crédito, el botón "Corregir a crédito" llama a
 * `marcar_compra_a_credito()` (crea la cuenta por pagar que faltaba) y,
 * solo si esa compra de verdad generó un egreso de caja por error, un
 * segundo botón "Compensar en caja" llama a
 * `compensar_egreso_compra_credito()` (ver
 * supabase/schema_compras_corregir_credito.sql) -- dos pasos separados
 * a propósito, para no mover plata de caja como efecto colateral
 * silencioso de una simple corrección de datos. Ambas acciones son
 * exclusivas del administrador. */
export function ComprasList() {
  const { perfil } = useAuth();
  const admin = isAdmin(perfil?.rol);

  const [errorAccion, setErrorAccion] = useState<string | null>(null);
  const [mensajeAccion, setMensajeAccion] = useState<string | null>(null);
  const [corrigiendo, setCorrigiendo] = useState<string | null>(null);
  const [compensando, setCompensando] = useState<string | null>(null);
  const [confirmarCompensar, setConfirmarCompensar] = useState<string | null>(null);

  const { data, loading, error, refetch } = useSupabaseQuery<CompraListItem[]>(async () => {
    const { data, error } = await supabase
      .from('compras')
      .select('*, proveedores ( nombre )')
      .order('fecha', { ascending: false })
      .limit(100);

    if (error) return { data: null, error };

    const compraIds = (data ?? []).map((row) => (row as { id: string }).id);

    // La forma de pago real no vive en una columna de `compras` -- se
    // deriva de si existe una `cuenta_por_pagar` para esa compra (crédito)
    // o de si generó un egreso de caja con origen='compra' (contado). Se
    // consultan aparte porque no hay una única relación directa que las
    // traiga a ambas en el mismo select.
    let idsConCuenta = new Set<string>();
    let idsConEgreso = new Set<string>();
    let idsConCompensacion = new Set<string>();

    if (compraIds.length > 0) {
      const { data: cuentas, error: errCuentas } = await supabase
        .from('cuentas_por_pagar')
        .select('compra_id')
        .in('compra_id', compraIds);
      if (errCuentas) return { data: null, error: errCuentas };
      idsConCuenta = new Set((cuentas ?? []).map((c) => c.compra_id));

      const { data: movimientos, error: errMovimientos } = await supabase
        .from('movimientos_caja')
        .select('referencia_id, tipo')
        .eq('origen', 'compra')
        .in('referencia_id', compraIds);
      if (errMovimientos) return { data: null, error: errMovimientos };

      for (const m of movimientos ?? []) {
        if (!m.referencia_id) continue;
        if (m.tipo === 'egreso') idsConEgreso.add(m.referencia_id);
        if (m.tipo === 'ingreso') idsConCompensacion.add(m.referencia_id);
      }
    }

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as CompraListItem),
      proveedor_nombre: (row.proveedores as { nombre: string } | null)?.nombre ?? '—',
      tiene_cuenta_por_pagar: idsConCuenta.has(row.id as string),
      tiene_egreso_caja: idsConEgreso.has(row.id as string),
      tiene_compensacion_caja: idsConCompensacion.has(row.id as string),
    }));

    return { data: items, error: null };
  });

  async function corregirACredito(id: string) {
    setErrorAccion(null);
    setMensajeAccion(null);
    setCorrigiendo(id);

    const { error: err } = await supabase.rpc('marcar_compra_a_credito', { p_compra_id: id });

    setCorrigiendo(null);

    if (err) {
      setErrorAccion(err.message);
      return;
    }

    setMensajeAccion('Compra corregida a crédito. Se creó la cuenta por pagar correspondiente.');
    refetch();
  }

  async function compensarEnCaja(id: string) {
    setErrorAccion(null);
    setMensajeAccion(null);
    setCompensando(id);

    const { error: err } = await supabase.rpc('compensar_egreso_compra_credito', { p_compra_id: id });

    setCompensando(null);
    setConfirmarCompensar(null);

    if (err) {
      setErrorAccion(err.message);
      return;
    }

    setMensajeAccion('Se registró el ingreso de compensación en la caja abierta.');
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Compras"
        description="Compras a proveedores — DAF v2, sección 17"
        actions={
          <div className="flex gap-2">
            <Link to="/admin/proveedores" className="btn-secondary">
              Proveedores
            </Link>
            <Link to="/admin/compras/cuentas-por-pagar" className="btn-secondary">
              Cuentas por pagar
            </Link>
            <Link to="/admin/compras/nueva" className="btn-primary">
              Nueva compra
            </Link>
          </div>
        }
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}
      {errorAccion && <p className="mb-4 text-sm text-red-600">{errorAccion}</p>}
      {mensajeAccion && <p className="mb-4 text-sm text-green-700">{mensajeAccion}</p>}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}

      {data && data.length === 0 && (
        <EmptyState title="No hay compras registradas" description="Registra la primera desde el botón de arriba." />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Proveedor</th>
                <th className="px-4 py-3 font-medium">Total</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Forma de pago</th>
                {admin && <th className="px-4 py-3 font-medium">Corrección</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx/60">
                    {new Date(c.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-onyx">{c.proveedor_nombre}</td>
                  <td className="px-4 py-3 font-medium text-onyx">{COP.format(c.total)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`badge ${
                        c.estado === 'anulada'
                          ? 'bg-red-100 text-red-700'
                          : c.estado === 'confirmada'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {c.estado}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`badge ${c.tiene_cuenta_por_pagar ? 'bg-blue-100 text-blue-800' : 'bg-gray-200 text-gray-700'}`}>
                      {c.tiene_cuenta_por_pagar ? 'Crédito' : 'Contado'}
                    </span>
                  </td>
                  {admin && (
                    <td className="px-4 py-3">
                      {c.estado !== 'confirmada' ? (
                        <span className="text-xs text-onyx/40">—</span>
                      ) : !c.tiene_cuenta_por_pagar ? (
                        <button
                          type="button"
                          disabled={corrigiendo === c.id}
                          className="btn-secondary px-3 py-1 text-xs"
                          onClick={() => corregirACredito(c.id)}
                        >
                          {corrigiendo === c.id ? 'Corrigiendo…' : 'Corregir a crédito'}
                        </button>
                      ) : c.tiene_compensacion_caja ? (
                        <span className="badge bg-green-100 text-green-800">Compensado en caja</span>
                      ) : c.tiene_egreso_caja ? (
                        confirmarCompensar === c.id ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-onyx/70">¿Confirmar?</span>
                            <button
                              type="button"
                              disabled={compensando === c.id}
                              className="btn-primary px-3 py-1 text-xs"
                              onClick={() => compensarEnCaja(c.id)}
                            >
                              {compensando === c.id ? 'Compensando…' : 'Sí, compensar'}
                            </button>
                            <button type="button" className="btn-secondary px-3 py-1 text-xs" onClick={() => setConfirmarCompensar(null)}>
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn-secondary px-3 py-1 text-xs text-amber-800"
                            onClick={() => setConfirmarCompensar(c.id)}
                          >
                            Compensar en caja
                          </button>
                        )
                      ) : (
                        <span className="text-xs text-onyx/40">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
